import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import {
  CURRENT_STOCK_QUERY_KEY,
  STOCK_COUNT_URL,
  STOCK_LOGS_QUERY_KEY
} from './queryConstants'
import { MedicationStockReason, StockCheckData } from '../types'
import { useUpdateStockRecord } from './useUpdateStockRecord'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import dayjs from 'dayjs'

type UseStockCheckProps = {
  residentId: string
  residentMedicationId: string
  expectedStock: number
  medicationName?: string
  onSuccess?: () => void
  onError?: (error: any) => void
}

export const useStockCheck = ({
  residentId,
  residentMedicationId,
  expectedStock,
  medicationName,
  onSuccess,
  onError
}: UseStockCheckProps) => {
  const queryClient = useQueryClient()
  const sendEvent = useMixpanelEvent()

  const { updateStockRecord, isLoading: isUpdating } = useUpdateStockRecord({
    onSuccess,
    onError
  })

  const createMutation = useMutation(
    (data: StockCheckData) => {
      const hasDiscrepancy =
        !data.isExpectedStockAccurate && data.stockCount !== expectedStock
      const resolveDiscrepancy =
        hasDiscrepancy && data.resolveDiscrepancy === 'yes'

      const formData = new FormData()

      formData.append('residentId', residentId)
      formData.append('residentMedicationId', residentMedicationId)
      formData.append('timeLogged', new Date().toISOString())
      formData.append('stockCount', String(data.stockCount))
      formData.append('notes', data.description)
      if (resolveDiscrepancy && data.discrepancyReason) {
        formData.append('discrepancyReason', data.discrepancyReason)
      }
      formData.append('resolveDiscrepancy', String(resolveDiscrepancy))

      if (data.media && data.media.length > 0) {
        data.media.forEach((file) => {
          formData.append('media', file)
        })
      }

      return postJSON({
        url: STOCK_COUNT_URL,
        contentType: 'multipart/form-data',
        body: formData
      })
    },
    {
      onSuccess: (_, variables) => {
        const hasDiscrepancy =
          !variables.isExpectedStockAccurate &&
          variables.stockCount !== expectedStock
        const resolveDiscrepancy =
          hasDiscrepancy && variables.resolveDiscrepancy === 'yes'

        if (medicationName) {
          sendEvent(MixpanelEvents.EMAR_V2_STOCK_RECORD_CREATED, {
            medicationName,
            typeOfRecord: 'Stock Check',
            time: dayjs().toISOString(),
            discrepanciesFlagged: hasDiscrepancy,
            discrepanciesClosed: resolveDiscrepancy
          })
        }

        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        queryClient.invalidateQueries([CURRENT_STOCK_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error) => {
        console.error('Error saving stock check:', error)
        onError?.(error)
      }
    }
  )

  const updateStockCheck = async ({
    recordId,
    data,
    actionType
  }: {
    recordId: string
    data: StockCheckData
    actionType: MedicationStockReason
  }) => {
    const hasDiscrepancy =
      !data.isExpectedStockAccurate && data.stockCount !== expectedStock
    const resolveDiscrepancy =
      hasDiscrepancy && data.resolveDiscrepancy === 'yes'

    return updateStockRecord({
      recordId,
      type: actionType,
      data: {
        notes: data.description,
        discrepancyReason: resolveDiscrepancy
          ? data.discrepancyReason
          : undefined,
        witnessIds: [],
        media: data.media,
        mediaAttachmentKeysToRemove: data.mediaAttachmentKeysToRemove
      }
    })
  }

  return {
    createStockCheck: createMutation.mutateAsync,
    updateStockCheck,
    isLoading: createMutation.isLoading || isUpdating
  }
}

export default useStockCheck
